import React, { useState, useEffect, useContext } from "react";
import { AuthContext, API } from "../App";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import {
  LayoutDashboard, Building2, Users, AlertTriangle, CalendarX, ClipboardList,
  ChevronRight, Loader2,
} from "lucide-react";

const Stat = ({ icon: Icon, label, value, tone = "slate", testid }) => (
  <Card className="border-slate-200" data-testid={testid}>
    <CardContent className="p-4 flex items-center gap-3">
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center bg-${tone}-100`}>
        <Icon className={`w-5 h-5 text-${tone}-600`} />
      </div>
      <div>
        <div className="text-2xl font-bold text-slate-900 leading-none">{value}</div>
        <div className="text-xs text-slate-500 mt-1">{label}</div>
      </div>
    </CardContent>
  </Card>
);

export default function RollUp() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(`${API}/rollup`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) setData(await res.json());
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-slate-500">
        <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading overview…
      </div>
    );
  }
  if (!data) {
    return <div className="p-8 text-slate-500">Could not load the overview.</div>;
  }

  const isPlatform = data.scope === "platform";

  return (
    <div className="max-w-6xl mx-auto px-4 py-6" data-testid="rollup-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-11 h-11 rounded-xl bg-slate-800 flex items-center justify-center">
          <LayoutDashboard className="w-6 h-6 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            {isPlatform ? "Platform Overview" : "Head-Office Overview"}
          </h1>
          <p className="text-sm text-slate-500">
            {isPlatform ? "Across all companies" : "Across all your company's sites"}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-8">
        <Stat icon={Building2} label="Sites" value={data.sites} tone="blue" testid="rollup-stat-sites" />
        <Stat icon={Users} label={isPlatform ? "Users" : "Team"} value={data.team} tone="violet" testid="rollup-stat-team" />
        <Stat icon={AlertTriangle} label="Open defects" value={data.open_defects} tone="rose" testid="rollup-stat-defects" />
        <Stat icon={ClipboardList} label="Overdue tasks" value={data.overdue_tasks} tone="amber" testid="rollup-stat-tasks" />
        <Stat icon={CalendarX} label="No diary today" value={data.diaries_missing_today} tone="orange" testid="rollup-stat-diaries" />
      </div>

      {isPlatform && (
        <Card className="mb-8 border-slate-200">
          <CardHeader><CardTitle className="text-base">Companies</CardTitle></CardHeader>
          <CardContent className="p-0">
            <div className="divide-y" data-testid="rollup-companies">
              {(data.companies || []).map((c) => (
                <div key={c.account_id} className="flex items-center justify-between px-5 py-3 text-sm">
                  <span className="font-medium text-slate-800">{c.name}</span>
                  <span className="text-slate-500">
                    {c.sites} sites · {c.team} users · {c.open_defects} open defects · {c.overdue_tasks} overdue
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="border-slate-200">
        <CardHeader><CardTitle className="text-base">Needs attention</CardTitle></CardHeader>
        <CardContent className="p-0">
          {(!data.needs_attention || data.needs_attention.length === 0) ? (
            <p className="px-5 py-6 text-sm text-slate-500">Everything looks up to date. 🎉</p>
          ) : (
            <div className="divide-y" data-testid="rollup-attention">
              {data.needs_attention.map((a) => (
                <button
                  key={a.site_id}
                  onClick={() => navigate("/defects")}
                  className="w-full flex items-center justify-between px-5 py-3 text-left hover:bg-slate-50 transition-colors"
                  data-testid={`rollup-attention-${a.site_id}`}
                >
                  <div>
                    <div className="font-medium text-slate-800">{a.name}</div>
                    <div className="text-xs text-slate-500">{a.reasons.join(" · ")}</div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-400" />
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
